import axios, { AxiosRequestConfig } from "axios";

import { REACT_APP_SERVER_LINK } from "utils";
import { IClient, ICreateClientPayload } from "Routes/Clients/types";

// Browser-local recycle bin for deleted clients: an entry keeps what is needed to re-create the
// client and expires after RECYCLE_RETENTION_DAYS. (The module bin went away with the legacy
// module editor.)

const isBrowser = typeof window !== "undefined";

const CLIENT_BIN_STORAGE_KEY = "meta16labs:client-recycle-bin";

export const CLIENT_RECYCLE_EVENT = "clientRecycleBin:update";
export const RECYCLE_RETENTION_DAYS = 30;
const RETENTION_MS = RECYCLE_RETENTION_DAYS * 24 * 60 * 60 * 1000;

const readStorage = <T>(key: string, fallback: T): T => {
  if (!isBrowser) {
    return fallback;
  }
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch (_err) {
    return fallback;
  }
};

const writeStorage = (key: string, value: unknown) => {
  if (!isBrowser) {
    return;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_err) {
    // no-op
  }
};

const emitEvent = (eventName: string, detail: unknown) => {
  if (!isBrowser || typeof window.dispatchEvent !== "function") {
    return;
  }
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
};

const getAuthConfig = (): AxiosRequestConfig | null => {
  if (!isBrowser) {
    return null;
  }
  try {
    const rawToken = window.localStorage.getItem("jwt");
    const token = rawToken ? JSON.parse(rawToken) : null;
    if (!token) {
      return null;
    }
    return {
      headers: {
        Authorization: token,
      },
    };
  } catch (_err) {
    return null;
  }
};

// Client recycle bin utilities
export type ClientRecycleBinEntry = {
  binId: string;
  clientId: string;
  clientName: string;
  deletedAt: number;
  clientData: IClient;
  payload: ICreateClientPayload;
};

export type ClientRecycleEventDetail =
  | {
      action: "deleted";
      clientId: string;
      client: IClient;
    }
  | {
      action: "restored";
      clientId: string;
      client: IClient;
    }
  | {
      action: "permanentlyDeleted";
      clientId: string;
      clientName: string;
    };

// Older bin entries were saved before slug/uri/deployment existed; their address was `website`.
type LegacyClientFields = { website?: string };

const convertClientToPayload = (client: IClient): ICreateClientPayload => {
  const lifecycleStatus: ICreateClientPayload["lifecycleStatus"] =
    client.lifecycleStatus === "active" ? "active" : "disabled";
  const legacy = client as IClient & LegacyClientFields;

  return {
    name: client.name,
    slug: client.slug || undefined,
    uri: client.uri || legacy.website || "",
    deployment: client.deployment || null,
    lifecycleStatus,
    contacts: (client.contacts ?? []).map((contact) => ({
      name: contact.name,
      role: contact.role,
      email: contact.email,
      phone: contact.phone,
    })),
    notes: client.notes,
  };
};

const purgeExpiredClientEntries = (
  entries: ClientRecycleBinEntry[]
): { valid: ClientRecycleBinEntry[]; changed: boolean } => {
  const now = Date.now();
  const valid: ClientRecycleBinEntry[] = [];
  let changed = false;

  entries.forEach((entry) => {
    if (!entry || !entry.clientId) {
      changed = true;
      return;
    }
    if (now - entry.deletedAt >= RETENTION_MS) {
      changed = true;
      emitEvent(CLIENT_RECYCLE_EVENT, {
        action: "permanentlyDeleted",
        clientId: entry.clientId,
        clientName: entry.clientName,
      } as ClientRecycleEventDetail);
      return;
    }
    valid.push(entry);
  });

  return { valid, changed };
};

export const getClientRecycleBinEntries = (): ClientRecycleBinEntry[] => {
  const entries = readStorage<ClientRecycleBinEntry[]>(CLIENT_BIN_STORAGE_KEY, []);
  const { valid, changed } = purgeExpiredClientEntries(entries);
  if (changed) {
    writeStorage(CLIENT_BIN_STORAGE_KEY, valid);
  }
  return [...valid].sort((a, b) => b.deletedAt - a.deletedAt);
};

export const addClientToRecycleBin = (client: IClient): ClientRecycleBinEntry | null => {
  if (!client || typeof client !== "object" || !client._id) {
    console.warn("Unable to cache deleted client – invalid payload", client);
    return null;
  }

  const normalizedClient: IClient = {
    ...client,
    contacts: Array.isArray(client.contacts) ? client.contacts : [],
  };

  const payload = convertClientToPayload(normalizedClient);
  const entries = getClientRecycleBinEntries();
  const deletedAt = Date.now();
  const entry: ClientRecycleBinEntry = {
    binId: `${normalizedClient._id}:${deletedAt}`,
    clientId: normalizedClient._id,
    clientName: normalizedClient.name,
    deletedAt,
    clientData: normalizedClient,
    payload,
  };

  writeStorage(CLIENT_BIN_STORAGE_KEY, [entry, ...entries]);
  emitEvent(CLIENT_RECYCLE_EVENT, {
    action: "deleted",
    clientId: normalizedClient._id,
    client: normalizedClient,
  } as ClientRecycleEventDetail);
  return entry;
};

export const restoreClientFromRecycleBin = async (binId: string): Promise<IClient | null> => {
  const entries = getClientRecycleBinEntries();
  const index = entries.findIndex((entry) => entry.binId === binId);
  if (index === -1) {
    return null;
  }

  const entry = entries[index];
  const authConfig = getAuthConfig();
  if (!authConfig) {
    throw new Error("Authentication required to restore client.");
  }

  // Rebuilt from the stored client so entries saved in the old format still restore.
  const payload = convertClientToPayload(entry.clientData);
  const { data } = await axios.post<IClient>(
    `${REACT_APP_SERVER_LINK}/clients`,
    payload,
    authConfig
  );

  const nextEntries = [...entries];
  nextEntries.splice(index, 1);
  writeStorage(CLIENT_BIN_STORAGE_KEY, nextEntries);
  emitEvent(CLIENT_RECYCLE_EVENT, {
    action: "restored",
    clientId: data._id,
    client: data,
  } as ClientRecycleEventDetail);
  return data;
};

export const permanentlyDeleteClientFromRecycleBin = (
  binId: string
): ClientRecycleBinEntry | null => {
  const entries = getClientRecycleBinEntries();
  const index = entries.findIndex((entry) => entry.binId === binId);
  if (index === -1) {
    return null;
  }

  const nextEntries = [...entries];
  const [entry] = nextEntries.splice(index, 1);
  writeStorage(CLIENT_BIN_STORAGE_KEY, nextEntries);
  emitEvent(CLIENT_RECYCLE_EVENT, {
    action: "permanentlyDeleted",
    clientId: entry.clientId,
    clientName: entry.clientName,
  } as ClientRecycleEventDetail);
  return entry;
};

export const purgeExpiredClientRecycleEntries = () => {
  getClientRecycleBinEntries();
};
